import { Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

interface ErrorData {
    error?: string;
    http_status_code?: number;
    message?: string;
    traceback?: string;
    wrapped_exception?: {
        error?: string
        message?: string
        traceback?: string
    };
}

interface ErrorDialogProps {
    open: boolean;
    onClose: () => void;
    errorData: ErrorData;
}

export const ErrorDialog: React.FC<ErrorDialogProps> = ({ open, onClose, errorData }) => {
    const [showTraceback, setShowTraceback] = useState(false);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Error {errorData.http_status_code || ""}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body1" gutterBottom>
                    {errorData.message || "An unknown error occurred."}
                </Typography>

                <Button onClick={() => setShowTraceback(!showTraceback)} size="small">
                    {showTraceback ? "Hide Traceback" : "Show Traceback"}
                </Button>

                <Collapse in={showTraceback}>
                    <Box mt={2} sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        <Typography variant="body2" color="textSecondary">
                            {errorData.traceback}
                        </Typography>
                        {/* Wrapped exception details */}
                        {errorData.wrapped_exception && (
                            <Box mt={2}>
                                {errorData.wrapped_exception.error && (
                                    <Typography variant="body2" fontWeight="bold" color="error">
                                        Wrapped Error: {errorData.wrapped_exception.error}
                                    </Typography>
                                )}
                                {errorData.wrapped_exception.message && (
                                    <Typography variant="body2" gutterBottom>
                                        {errorData.wrapped_exception.message}
                                    </Typography>
                                )}
                                {errorData.wrapped_exception.traceback && (
                                    <Typography variant="body2" color="textSecondary">
                                        {errorData.wrapped_exception.traceback}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export function useErrorDialog() {
    const [errorData, setErrorData] = useState<any | null>(null);
    const [open, setOpen] = useState(false);

    function showErrorDialog(error: any): void {
        if (
            error &&
            error.response &&
            error.response.data
        ) {
            setErrorData(error.response.data);
            setOpen(true);
        } else {
            const statusCode = (error && error.response && error.response.status) ? error.response.status : 500;

            setErrorData({
                http_status_code: statusCode,
                message: "Unknown error occurred.",
                traceback: ""
            });
            setOpen(true);
        }
    }

    const dialog = errorData ? (
        <ErrorDialog
            open={open}
            onClose={() => setOpen(false)}
            errorData={errorData}
        />
    ) : null;

    return { showErrorDialog, dialog };
}

export async function handleErrorMessage(
    error: any,
    setErrorMessage: (message: string) => void,
    history: ReturnType<typeof useHistory>,
    location: ReturnType<typeof useLocation>,
    showToast: boolean = true // optional toast flag
): Promise<void> {
    console.error(error);

    let finalMessage = "Unknown error occurred";

    if (typeof error === "string") {
        finalMessage = error;
        setErrorMessage(finalMessage);
        if (showToast) toast.error(finalMessage);
        return;
    }

    if (axios.isAxiosError(error)) {
        if (error.response && error.response.status === 401) {
            history.push('/login', {
                from: {
                    pathname: location.pathname,
                    search: location.search,
                },
            });
            return;
        }

        if (error.response) {
            const { data } = error.response;

            if (data instanceof Blob) {
                try {
                    const text = await data.text();
                    if (data.type === "application/json") {
                        const json = JSON.parse(text);
                        finalMessage = json.message || finalMessage;
                        setErrorMessage(finalMessage);
                        if (showToast) toast.error(finalMessage);
                    } else {
                        finalMessage = text;
                        setErrorMessage(finalMessage);
                        if (showToast) toast.error(finalMessage);
                    }
                } catch (parseError) {
                    console.error("Failed to parse JSON Error from Blob:", parseError);
                    finalMessage = error.message;
                    setErrorMessage(finalMessage);
                    if (showToast) toast.error(finalMessage);
                }
            } else {
                finalMessage = data && data.message ? data.message : error.message;
                if (data.wrapped_exception && data.wrapped_exception.message)
                    finalMessage += " :: " + data.wrapped_exception.message
                setErrorMessage(finalMessage);
                if (showToast) toast.error(finalMessage);
            }
        } else {
            finalMessage = error.message;
            setErrorMessage(finalMessage);
            if (showToast) toast.error(finalMessage);
        }
    } else {
        throw error;
    }
}